import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

export default function AdminScan() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const scannerRef = useRef(null);
  const scannerInstanceRef = useRef(null);

  const startScanner = async () => {
    setResult(null);
    setError('');
    setScanning(true);

    try {
      const { Html5QrcodeScanner } = await import('html5-qrcode');
      setTimeout(() => {
        scannerInstanceRef.current = new Html5QrcodeScanner(
          'reader',
          { fps: 10, qrbox: { width: 280, height: 280 }, aspectRatio: 1.0 },
          false
        );
        scannerInstanceRef.current.render(
          async (decodedText) => {
            // Extract ticket number — QR may be plain ticket number like "ALS-XXXXXX"
            const ticketNumber = decodedText.trim().split('/').pop().toUpperCase();
            await stopScanner();
            await lookupTicket(ticketNumber);
          },
          (err) => { /* suppress camera errors */ }
        );
      }, 100);
    } catch {
      setError('Camera not available. Try manual entry below.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerInstanceRef.current) {
      try { await scannerInstanceRef.current.clear(); } catch {}
      scannerInstanceRef.current = null;
    }
    setScanning(false);
  };

  const lookupTicket = async (ticketNumber) => {
    setError('');
    setResult(null);
    try {
      const res = await api.post(`/admin/tickets/scan/${ticketNumber}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setResult({ found: false, ticketNumber });
      } else {
        setError('Lookup failed. Check connection.');
      }
    }
  };

  const handleCheckIn = async () => {
    if (!result?._id) return;
    setCheckingIn(true);
    try {
      await api.post(`/admin/tickets/${result._id}/checkin`);
      setResult(r => ({ ...r, checkedIn: true, checkedInAt: new Date().toISOString() }));
    } finally {
      setCheckingIn(false);
    }
  };

  useEffect(() => () => { if (scannerInstanceRef.current) try { scannerInstanceRef.current.clear(); } catch {} }, []);

  const [manualInput, setManualInput] = useState('');

  const getResultClass = () => {
    if (!result) return '';
    if (!result.found) return 'not-found';
    return result.paymentStatus;
  };

  const getResultTitle = () => {
    if (!result) return '';
    if (!result.found) return '❌ Ticket Not Found';
    if (result.paymentStatus === 'approved') return '✅ Valid Ticket';
    if (result.paymentStatus === 'free') return '✅ Free Ticket — Valid';
    if (result.paymentStatus === 'declined') return '🚫 Payment Not Received';
    return '⏳ Pending Payment Approval';
  };

  return (
    <div className="scan-page">
      <div className="admin-page-header" style={{ textAlign: 'center' }}>
        <h1 className="admin-page-title">QR Scanner</h1>
        <p className="admin-page-sub">Scan attendee ticket QR codes at the entrance</p>
      </div>

      {/* Big Scan Button */}
      {!scanning && (
        <div className="scan-btn-wrap">
          <button className="scan-big-btn" onClick={startScanner}>
            <span style={{ fontSize: 32 }}>📷</span>
            <div>
              <div>Open Camera Scanner</div>
              <div style={{ fontSize: 13, fontWeight: 400, opacity: 0.7, marginTop: 2 }}>Tap to scan QR code</div>
            </div>
          </button>
        </div>
      )}

      {/* Stop button */}
      {scanning && (
        <div style={{ marginBottom: 16 }}>
          <button className="admin-btn admin-btn-ghost" onClick={stopScanner} style={{ margin: '0 auto 16px', display: 'block' }}>
            Stop Scanner
          </button>
        </div>
      )}

      {/* Scanner container */}
      <div id="reader" ref={scannerRef} style={{ display: scanning ? 'block' : 'none' }} />

      {/* Manual entry */}
      <div style={{ maxWidth: 400, margin: '24px auto', display: 'flex', gap: 8 }}>
        <input
          className="admin-input" placeholder="Manual: enter ticket # (ALS-XXXXXX)"
          value={manualInput} onChange={e => setManualInput(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter' && manualInput.trim()) { lookupTicket(manualInput.trim()); setManualInput(''); } }}
        />
        <button className="admin-btn" onClick={() => { lookupTicket(manualInput.trim()); setManualInput(''); }} disabled={!manualInput.trim()}>
          Check
        </button>
      </div>

      {error && <p style={{ color: '#f87171', textAlign: 'center', fontSize: 14 }}>{error}</p>}

      {/* Scan Result */}
      {result && (
        <div className={`scan-result ${getResultClass()}`}>
          <div className="scan-result-title">{getResultTitle()}</div>

          {result.found ? (
            <>
              <div className="scan-result-row"><label>Ticket #</label><span>{result.ticketNumber}</span></div>
              <div className="scan-result-row"><label>Name</label><span>{result.fullName}</span></div>
              <div className="scan-result-row"><label>Phone</label><span>{result.phoneNumber}</span></div>
              <div className="scan-result-row"><label>Type</label><span>{result.isFreeTicket ? 'Free Ticket' : 'Paid Ticket'}</span></div>
              <div className="scan-result-row"><label>Status</label><span style={{ textTransform: 'capitalize' }}>{result.paymentStatus}</span></div>
              <div className="scan-result-row">
                <label>Check-in</label>
                <span>{result.checkedIn ? `✓ Already checked in` : 'Not yet'}</span>
              </div>

              {(result.paymentStatus === 'approved' || result.paymentStatus === 'free') && !result.checkedIn && (
                <button
                  className="admin-btn" style={{ width: '100%', marginTop: 16, padding: 14 }}
                  onClick={handleCheckIn} disabled={checkingIn}
                >
                  {checkingIn ? 'Processing...' : '✓ Mark as Checked In'}
                </button>
              )}

              {result.checkedIn && (
                <div style={{ marginTop: 16, padding: '12px', background: 'rgba(99,102,241,0.1)', borderRadius: 10, textAlign: 'center', fontSize: 14, color: '#a5b4fc' }}>
                  ✓ Already Checked In
                </div>
              )}
            </>
          ) : (
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>No ticket found with number: <strong>{result.ticketNumber}</strong></p>
          )}

          <button
            className="admin-btn admin-btn-ghost" style={{ width: '100%', marginTop: 12, padding: 12 }}
            onClick={() => { setResult(null); }}
          >
            Scan Another
          </button>
        </div>
      )}
    </div>
  );
}
